import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearch } from "wouter";
import Header from "@/components/Header";
import { Card, CardContent } from "@/components/ui/card";
import { MessageCircle, Loader2, ChevronRight, Sparkles } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import OrderChat from "@/components/OrderChat";
import SupportChat from "@/components/SupportChat";
import { FeedbackChat } from "@/components/FeedbackChat";

type OrderWithMessages = {
  orderId?: string;
  orderRef?: string;
  otherPartyName: string;
  otherPartyId?: string;
  lastMessage: string;
  lastMessageAt: string;
  unreadCount: number;
  role?: "buyer" | "seller";
  kind?: "support" | "admin_support";
  feedbackId?: string;
};

function convKey(c: OrderWithMessages): string {
  if (c.kind === "admin_support") return `admin_support:${c.otherPartyId ?? ""}`;
  return c.feedbackId ? `support:${c.feedbackId}` : (c.orderId ?? "");
}

type RoleFilter = "buyer" | "seller";

export default function Messages() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const search = useSearch();
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("buyer");
  const autoExpandedRef = useRef(false);

  const { data: conversations = [], isLoading } = useQuery<OrderWithMessages[]>({
    queryKey: ["/api/my/conversations"],
    queryFn: async () => {
      const res = await fetch("/api/my/conversations", { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    refetchInterval: 15_000,
  });

  // Optimistically zero this conversation's unread count the moment the user
  // expands it. OrderChat will fire POST /mark-read on mount and the refetch
  // will reconcile, but the card badge drops to 0 without waiting for the
  // round-trip.
  const expandConversation = (key: string | null) => {
    setExpandedOrderId((prev) => {
      const next = prev === key ? null : key;
      if (next) {
        queryClient.setQueryData<OrderWithMessages[]>(
          ["/api/my/conversations"],
          (current = []) =>
            current.map((c) => (convKey(c) === next ? { ...c, unreadCount: 0 } : c)),
        );
      }
      return next;
    });
  };

  // Deep link from a support_message notification: /messages?support=1
  // auto-expands the Stoop Support thread once it has loaded.
  useEffect(() => {
    if (autoExpandedRef.current) return;
    if (!new URLSearchParams(search).has("support")) return;
    const supportConv = conversations.find((c) => c.kind === "admin_support");
    if (supportConv) {
      autoExpandedRef.current = true;
      expandConversation(convKey(supportConv));
    }
  }, [search, conversations]);

  const { hasBuyer, hasSeller, buyerUnread, sellerUnread, filtered, supportConvos } = useMemo(() => {
    const isSupportKind = (c: OrderWithMessages) =>
      c.kind === "support" || c.kind === "admin_support";
    const support = conversations.filter(isSupportKind);
    const buyer = conversations.filter((c) => c.role === "buyer" && !isSupportKind(c));
    const seller = conversations.filter((c) => c.role === "seller" && !isSupportKind(c));
    return {
      hasBuyer: buyer.length > 0,
      hasSeller: seller.length > 0,
      buyerUnread: buyer.reduce((sum, c) => sum + (c.unreadCount || 0), 0),
      sellerUnread: seller.reduce((sum, c) => sum + (c.unreadCount || 0), 0),
      filtered: roleFilter === "seller" ? seller : buyer,
      supportConvos: support,
    };
  }, [conversations, roleFilter]);

  const showTabs = hasBuyer && hasSeller;
  // Support ("Stoop") threads always show, on top, regardless of buyer/seller tab.
  const visibleConversations = [
    ...supportConvos,
    ...(showTabs
      ? filtered
      : conversations.filter((c) => c.kind !== "support" && c.kind !== "admin_support")),
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <div className="container mx-auto max-w-2xl px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight">Messages</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Your conversations with buyers and sellers
          </p>
        </div>

        {showTabs && (
          <div className="mb-4 inline-flex rounded-lg border bg-card p-1">
            <button
              type="button"
              onClick={() => { setRoleFilter("buyer"); setExpandedOrderId(null); }}
              className={`relative rounded-md px-4 py-1.5 text-sm font-medium transition ${
                roleFilter === "buyer" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
              }`}
            >
              As Buyer
              {buyerUnread > 0 && (
                <span className="ml-2 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                  {buyerUnread > 9 ? "9+" : buyerUnread}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => { setRoleFilter("seller"); setExpandedOrderId(null); }}
              className={`relative rounded-md px-4 py-1.5 text-sm font-medium transition ${
                roleFilter === "seller" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
              }`}
            >
              As Seller
              {sellerUnread > 0 && (
                <span className="ml-2 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                  {sellerUnread > 9 ? "9+" : sellerUnread}
                </span>
              )}
            </button>
          </div>
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && visibleConversations.length === 0 && (
          <div className="flex flex-col items-center gap-4 py-16 text-center">
            <MessageCircle className="h-12 w-12 text-muted-foreground/40" />
            <div>
              <h3 className="text-lg font-semibold">
                {showTabs
                  ? roleFilter === "seller"
                    ? "No seller conversations yet"
                    : "No buyer conversations yet"
                  : "No messages yet"}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Chat with a seller or buyer after placing an order.
              </p>
            </div>
          </div>
        )}

        {!isLoading && visibleConversations.length > 0 && (
          <div className="space-y-2">
            {visibleConversations.map((conv) => {
              const key = convKey(conv);
              const isSupport = conv.kind === "support" || conv.kind === "admin_support";
              const expanded = expandedOrderId === key;
              return (
                <Card key={key} className={conv.unreadCount > 0 ? "border-primary/30 bg-primary/5" : ""}>
                  <CardContent className="p-0">
                    <button
                      type="button"
                      className="flex w-full items-center gap-3 p-4 text-left"
                      onClick={() => expandConversation(key)}
                    >
                      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${isSupport ? "bg-primary/15" : "bg-muted"}`}>
                        {isSupport ? (
                          <Sparkles className="h-5 w-5 text-primary" />
                        ) : (
                          <MessageCircle className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate font-semibold text-sm">{conv.otherPartyName}</span>
                          {conv.unreadCount > 0 && (
                            <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                              {conv.unreadCount}
                            </span>
                          )}
                        </div>
                        <p className="truncate text-xs text-muted-foreground">
                          {isSupport ? conv.lastMessage : `Order #${conv.orderRef} · ${conv.lastMessage}`}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {(() => {
                            try {
                              return new Date(conv.lastMessageAt).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
                            } catch {
                              return "";
                            }
                          })()}
                        </p>
                      </div>
                      <ChevronRight className={`h-4 w-4 shrink-0 text-muted-foreground transition ${expanded ? "rotate-90" : ""}`} />
                    </button>

                    {expanded && (
                      <div className="border-t px-4 pb-4">
                        {conv.kind === "admin_support" && conv.otherPartyId ? (
                          <SupportChat
                            otherUserId={conv.otherPartyId}
                            otherPartyName={conv.otherPartyName}
                          />
                        ) : isSupport && conv.feedbackId ? (
                          <FeedbackChat feedbackId={conv.feedbackId} showReportContext />
                        ) : conv.orderId ? (
                          <OrderChat
                            orderId={conv.orderId}
                            otherPartyName={conv.otherPartyName}
                            recipientId={conv.otherPartyId ?? ""}
                          />
                        ) : null}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
